import React, { useMemo } from "react";
import { Streamdown } from "streamdown";
import { code } from "@streamdown/code";
import "streamdown/styles.css";
import type { UIMessage } from "ai";
import { isToolUIPart } from "ai";
import { ToolCallIndicator } from "./ToolCallIndicator";
import { MdDownloadLink, isMdDownload } from "./MdDownloadLink";
import { MdDownloadImage } from "./MdDownloadImage";

/** Regex to match @[DisplayName](metadata_json) mentions */
const MENTION_REGEX = /@\[([^\]]+)\]\([^)]+\)/g;

/**
 * Replace @[DisplayName](metadata) with clean @DisplayName badges.
 * Returns React nodes with styled mention spans.
 */
function renderMentions(text: string): React.ReactNode {
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let key = 0;

  // Reset regex state
  MENTION_REGEX.lastIndex = 0;

  while ((match = MENTION_REGEX.exec(text)) !== null) {
    // Text before mention
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    // Badge
    const displayName = match[1];
    parts.push(
      <span
        key={key++}
        className="inline-flex items-center bg-accent-violet/15 text-accent-violet rounded px-1 py-0 text-[12px] font-medium whitespace-nowrap"
      >
        @{displayName}
      </span>
    );
    lastIndex = match.index + match[0].length;
  }

  if (parts.length === 0) return null; // No mentions found
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }
  return <>{parts}</>;
}

interface MessageBubbleProps {
  message: UIMessage;
}

/** Convert rewritten md-download URL back to the original relative path */
function toOriginalMdPath(url: string): string {
  return url.replace(MD_DOWNLOAD_MARKER, "md_download/");
}

function isMdDownloadUrl(url: string | undefined | null): boolean {
  return !!url && (isMdDownload(url) || url.startsWith(MD_DOWNLOAD_MARKER));
}

// Custom link: intercepts md_download URLs
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function CustomLink({ href, children, node, ...rest }: any) {
  if (href && isMdDownloadUrl(href)) {
    return <MdDownloadLink href={toOriginalMdPath(href)}>{children}</MdDownloadLink>;
  }
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" {...rest}>
      {children}
    </a>
  );
}

// Custom image: fetches md_download images via authenticated API
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function CustomImage({ src, alt, node, ...rest }: any) {
  if (src && isMdDownloadUrl(src)) {
    return <MdDownloadImage src={toOriginalMdPath(src)} alt={alt} />;
  }
  return <img src={src} alt={alt} {...rest} className="max-w-full rounded-lg my-2" />;
}

const streamdownComponents = {
  a: CustomLink,
  img: CustomImage,
};

/**
 * Rewrite relative md_download/ URLs to absolute https://md-download/...
 * so they survive Streamdown's rehype-sanitize (which strips unknown protocols).
 * Custom components convert them back for the API call.
 */
const MD_DOWNLOAD_MARKER = "https://md-download/";

function rewriteMdDownloadUrls(text: string): string {
  return text.replace(
    /(\]\()md_download\//g,
    `$1${MD_DOWNLOAD_MARKER}`
  );
}

/** Extract the full text content from a UIMessage's text parts */
function getTextContent(message: UIMessage): string {
  return message.parts
    .filter((p): p is { type: "text"; text: string } => p.type === "text")
    .map((p) => p.text)
    .join("");
}

/** Check if a message is currently streaming text */
function isMessageStreaming(message: UIMessage): boolean {
  return message.parts.some(
    (p) => p.type === "text" && "state" in p && p.state === "streaming"
  );
}

export function MessageBubble({ message }: MessageBubbleProps) {
  const textContent = useMemo(() => rewriteMdDownloadUrls(getTextContent(message)), [message]);
  const streaming = isMessageStreaming(message);

  // Collect tool parts
  const toolParts = message.parts.filter((p) => isToolUIPart(p));

  // Pre-render mentions for user messages
  const userContent = useMemo(() => {
    if (message.role !== "user") return null;
    return renderMentions(textContent) ?? textContent;
  }, [message.role, textContent]);

  if (message.role === "user") {
    return (
      <div className="flex justify-end mb-4">
        <div className="cai-user-bubble px-3.5 py-2.5 rounded-2xl rounded-br-sm max-w-[85%] text-[13px] leading-relaxed text-text-primary">
          {userContent}
        </div>
      </div>
    );
  }

  return (
    <div className="mb-4">
      {/* Tool call indicators */}
      {toolParts.length > 0 && (
        <div className="ml-9 mb-2 space-y-1.5">
          {toolParts.map((part) => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const p = part as any;
            const toolName = p.toolName ?? p.type?.replace("tool-", "") ?? "unknown";
            return (
              <ToolCallIndicator
                key={p.toolCallId ?? toolName}
                toolName={toolName}
                toolCallId={p.toolCallId ?? ""}
                state={p.state ?? "input-available"}
                input={p.input}
                output={p.output}
                errorText={p.errorText}
              />
            );
          })}
        </div>
      )}

      {/* Assistant text content */}
      {textContent && (
        <div className="flex gap-2.5">
          <div className="w-6 h-6 cai-avatar rounded-full shrink-0 mt-0.5" />
          <div className="min-w-0 max-w-[90%] cai-glass px-3.5 py-3 rounded-2xl rounded-tl-sm text-[13px] leading-relaxed">
            <Streamdown
              plugins={{ code }}
              components={streamdownComponents}
              isAnimating={streaming}
              caret="block"
              controls={{ code: true, table: true }}
              shikiTheme={["github-light", "github-dark"]}
            >
              {textContent}
            </Streamdown>
          </div>
        </div>
      )}
    </div>
  );
}
